import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { X, ArrowLeft, Truck, ExternalLink, Loader2, Check } from 'lucide-react'
import { setDeliveryMode } from '@/lib/deliveryMode'
import {
  useDeliveryRuns,
  useDeliveryRun,
  DELIVERY_RUN_STATUS_LABEL,
  type DeliveryRun,
} from '@/api/deliveryRuns'
import { useSysConfig } from '@/api/config'
import { buildStaticMapUrl } from '@/lib/staticMap'
import { runProgress } from '@/lib/fulfillment'
import { DELIVERY_STATUS } from '@/api/receipts'

// Slide-over "runs rail": view active delivery runs — with a per-run progress
// map + stop list — without leaving the Orders tab. Read-only; run controls
// (dispatch / optimize / cancel) stay on the /deliveries board ("Open in
// Dispatch"). See docs/fulfillment-roadmap.md M1 §1.2–1.3 (slide-over variant).

const RUN_STATUS_TONE: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  started: 'bg-teal-100 text-teal-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-200 text-gray-600',
}

function RunStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${RUN_STATUS_TONE[status] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {DELIVERY_RUN_STATUS_LABEL[status] ?? status}
    </span>
  )
}

export function DeliveryRunsPanel({ onClose }: { onClose: () => void }) {
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const { data: runs, isLoading } = useDeliveryRuns(['open', 'started'])
  const { data: detail } = useDeliveryRun(selectedRunId)
  const { data: cfg } = useSysConfig(['kiosk.delivery.google_maps_api_key'])
  const mapsKey = (cfg?.['kiosk.delivery.google_maps_api_key'] as string) || ''

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md h-full bg-white flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {selectedRunId !== null ? (
          <RunDetailView
            run={detail}
            mapsKey={mapsKey}
            onBack={() => setSelectedRunId(null)}
            onClose={onClose}
          />
        ) : (
          <RunListView
            runs={runs ?? []}
            isLoading={isLoading}
            onSelect={setSelectedRunId}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  )
}

function RunListView({
  runs,
  isLoading,
  onSelect,
  onClose,
}: {
  runs: DeliveryRun[]
  isLoading: boolean
  onSelect: (id: number) => void
  onClose: () => void
}) {
  return (
    <>
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-blue-700" />
          <h2 className="font-semibold text-gray-900">Delivery runs</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </header>
      <div className="px-4 py-2 border-b border-gray-100">
        <Link
          to="/deliveries"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
        >
          Open Dispatch board <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : runs.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-12 px-6">
            No active runs. Select delivery orders and tap “Add to Delivery Run”
            to start one.
          </div>
        ) : (
          runs.map((run) => (
            <button
              key={run.id}
              type="button"
              onClick={() => onSelect(run.id)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-medium text-gray-900 truncate">
                  {run.name}
                </span>
                <RunStatusBadge status={run.status} />
              </div>
              <div className="mt-1.5 text-xs text-gray-500">
                {run.stop_count} stop{run.stop_count === 1 ? '' : 's'} ·{' '}
                {run.driver_name ?? 'No driver'}
              </div>
            </button>
          ))
        )}
      </div>
    </>
  )
}

function RunDetailView({
  run,
  mapsKey,
  onBack,
  onClose,
}: {
  run: DeliveryRun | undefined
  mapsKey: string
  onBack: () => void
  onClose: () => void
}) {
  const navigate = useNavigate()
  if (!run) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }
  const mapUrl = buildStaticMapUrl(run, mapsKey)
  const progress = runProgress(run.stops ?? [])

  return (
    <>
      <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-200">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-mono font-semibold text-gray-900 truncate">
            {run.name}
          </div>
          <div className="text-xs text-gray-500">
            {progress.deliveredCount}/{progress.total} delivered
          </div>
        </div>
        <RunStatusBadge status={run.status} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto">
        {mapUrl && (
          <img
            src={mapUrl}
            alt={`Route map for ${run.name}`}
            className="w-full aspect-[2/1] object-cover bg-gray-100"
          />
        )}
        <ol className="p-3 space-y-1">
          {progress.ordered.map((stop, i) => {
            const done = stop.delivery_status === DELIVERY_STATUS.DELIVERED
            const isCurrent = progress.current?.run_order_id === stop.run_order_id
            return (
              <li
                key={stop.run_order_id}
                className="flex items-start gap-2.5 p-2 rounded-lg"
              >
                <span
                  className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    done
                      ? 'bg-green-500 text-white'
                      : isCurrent
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {stop.customer_name ?? `#${stop.number ?? stop.receipt_id}`}
                  </div>
                  {stop.address && (
                    <div className="text-xs text-gray-500 truncate">
                      {[stop.address, stop.city].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </li>
            )
          })}
          {progress.total === 0 && (
            <li className="text-center text-sm text-gray-500 py-6">
              No stops on this run yet.
            </li>
          )}
        </ol>
      </div>
      {/* Onward paths. Mobile: Dispatch (board) + Driver mode (field) side by
          side. Desktop: only Dispatch — driving is a field/mobile action, so
          Driver mode is hidden and Dispatch goes full-width as the primary. */}
      <footer className="p-3 border-t border-gray-200 grid grid-cols-2 gap-2 md:grid-cols-1">
        <Link
          to="/deliveries"
          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold border border-blue-600 text-blue-700 hover:bg-blue-50 md:border-transparent md:bg-blue-600 md:text-white md:hover:bg-blue-700"
        >
          <ExternalLink className="w-4 h-4" /> Dispatch
        </Link>
        <button
          type="button"
          onClick={() => {
            setDeliveryMode(true)
            onClose()
            navigate({ to: '/driver' })
          }}
          className="md:hidden flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
        >
          <Truck className="w-4 h-4" /> Driver mode
        </button>
      </footer>
    </>
  )
}
